import React, { useCallback, useRef, useState } from "react";
import { Form, Input, Switch, Select, Modal as AntModal, Tooltip } from "antd";
import { EditOutlined, DeleteOutlined, CopyOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Modal, PageHeader } from "../../components/Common";
import PaginatedTable from "../../components/PaginatedTable";
import { useMutations } from "../../hooks/useApiMutation";
import { useConfirm } from "../../hooks/useConfirm";
import SearchSelect from "../../components/SearchSelect";
import { getMessage } from "../../Utilities/messageApi";

const ApiKeysList = () => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const { mutate } = useMutations();
  const { confirmAction } = useConfirm();
  const tableRef = useRef(null);

  const fetchData = useCallback(
    async (page, pageSize, search) => {
      try {
        const { data } = await mutate("fetchApiKeys", {
          method: "get",
          endpoint: "/apikeys",
          params: { page, pageSize, search },
        });
        return {
          data: data?.data?.items || [],
          total: data?.data?.totalCount || 0,
        };
      } catch {
        return { data: [], total: 0 };
      }
    },
    [mutate]
  );

  const columns = [
    { title: "Key", dataIndex: "key", key: "key" },
    { title: "Client", dataIndex: "clientName", key: "clientName" },
    { title: "Package", dataIndex: "packageName", key: "packageName" },
    {
      title: "Subscription",
      dataIndex: "subscriptionId",
      key: "subscriptionId",
      responsive: ["none"],
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (value) => (value || "").toUpperCase(),
    },
    {
      title: "Active",
      dataIndex: "subscriptionActive",
      key: "subscriptionActive",
      render: (v) => (v ? "Yes" : "No"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record) => (
        <>
          <Tooltip title="Edit"><Button variant="ghost" icon={<EditOutlined />} onClick={() => onEdit(record)} /></Tooltip>
          <Tooltip title="Delete"><Button
            variant="ghost"
            danger
            icon={<DeleteOutlined />}
            onClick={() =>
              confirmAction({
                key: "deleteApiKey",
                method: "delete",
                endpoint: `/apikeys/${record.key}`,
                title: "Delete Confirmation",
                content: "Are you sure you want to delete this item?",
                okText: "Delete",
                okType: "danger",
                onSuccess: () => tableRef.current?.reloadPage(),
              })
            }
          /></Tooltip>
          <Tooltip title="Copy"><Button
            variant="ghost"
            icon={<CopyOutlined />}
            onClick={() => {
              navigator.clipboard.writeText(record.key);
              const message = getMessage();
              message.info("API Key copied to clipboard!");
            }}
          /></Tooltip>
        </>
      ),
    },
  ];

  const onEdit = (record) => {
    setEditing(record);
    form.setFieldsValue(record);
    setOpen(true);
  };

  const onCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ type: "live", subscriptionActive: true });
    setOpen(true);
  };

  const handleOk = async () => {
    const values = await form.validateFields();
    if (editing) {
      await mutate("updateApiKey", {
        method: "put",
        endpoint: `/apikeys/${editing.key}`,
        payload: values,
      });
    } else {
      const { data } = await mutate("createApiKey", {
        method: "post",
        endpoint: "/apikeys",
        payload: values,
      });
      // TODO: extend shared Modal component to support info variant (no footer cancel/confirm)
      AntModal.info({
        title: "Generated API Key",
        content: data?.data?.key,
      });
    }
    tableRef.current?.reloadPage();
    setOpen(false);
  };

  return (
    <div>
      <PageHeader
        title="API Keys"
        subtitle="Issue and rotate the keys clients use to call the platform API."
        extra={
          <Button variant="primary" icon={<PlusOutlined />} onClick={onCreate}>
            Create API Key
          </Button>
        }
      />
      <PaginatedTable
        ref={tableRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="key"
      />
      <Modal
        title={editing ? "Edit API Key" : "Create API Key"}
        open={open}
        onConfirm={handleOk}
        onClose={() => setOpen(false)}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="key"
            label="Key"
            rules={editing ? [{ required: true }] : []}
            hidden={!editing}
          >
            <Input disabled />
          </Form.Item>
          <Form.Item
            name="subscriptionId"
            label="Subscription"
            rules={[{ required: true }]}
            hidden={!!editing}
          >
            <SearchSelect
              fetchKey="searchSubsForKeys"
              endpoint="/subscription/search"
              labelKey="description"
              valueKey="subscriptionId"
              placeholder="Select subscription"
            />
          </Form.Item>
          <Form.Item name="type" label="Environment" rules={[{ required: true }]}>
            <Select
              disabled={!!editing}
              options={[
                { value: "live", label: "Live" },
                { value: "test", label: "Test" },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="subscriptionActive"
            label="Active"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ApiKeysList;
